import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

export const PURCHASE_ECF_TYPES = [
  { value: "E41", label: "E41 · Compra de mercancía a persona informal" },
];

// Firma y envía (o re-emite en pruebas) el e-CF de una compra o un gasto.
export function EmitPurchaseEcfDialog({ purchase, onClose, onDone, endpoint = "purchases" }) {
  const accepted = purchase?.ecf_status === "aceptado";
  const type = purchase?.ncf_type || "E41";
  const [reemit, setReemit] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setBusy(true);
    try {
      const body = endpoint === "purchases" ? { ncf_type: type, reemit } : { reemit };
      const { data } = await api.post(`/${endpoint}/${purchase.id}/send-ecf`, body);
      if (data.ok === false) toast.error(data.error || data.message || "DGII no aceptó el e-CF");
      else toast.success(`e-CF ${type} enviado a DGII`);
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!purchase} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="emit-purchase-ecf-dialog">
        <DialogHeader><DialogTitle>Emitir e-CF {type} · {purchase?.number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="overline">Comprobante</Label>
            <div className="mt-1 font-mono text-sm" data-testid="emit-purchase-ecf-type">{purchase?.ncf || `${type} (se asigna de la secuencia)`}</div>
            <p className="text-xs text-muted-foreground mt-2">
              Amelia toma el siguiente e-NCF de la secuencia {type} del ambiente activo y lo firma con tu certificado.
            </p>
          </div>
          {accepted && (
            <label className="flex items-start gap-2 text-sm" data-testid="emit-purchase-ecf-reemit">
              <Checkbox checked={reemit} onCheckedChange={(v) => setReemit(!!v)} className="mt-0.5" />
              <span>Re-emitir con un <b>e-NCF nuevo</b> (solo pruebas DGII, cuando el paso se reinició). {purchase.ncf} quedará como descartado.</span>
            </label>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={send} disabled={busy || (accepted && !reemit)} className="bg-primary font-bold" data-testid="emit-purchase-ecf-send">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Firmar y enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
